// BuilderContext - マップエディター用のContext Provider
// シーンマネージャー、レンダラー、入力ハンドラーを共有

import { createContext, useContext, useState, useMemo, useCallback, ReactNode } from "react";
import { EventBus } from "engine";
import { Scene } from "@babylonjs/core/scene";
import { BuilderSceneManager } from "features/builder/engine";

interface BuilderContextValue {
  eventBus: EventBus;
  scene: Scene | null;
  setScene: (scene: Scene | null) => void;
  sceneManager: BuilderSceneManager | null;
  setSceneManager: (manager: BuilderSceneManager | null) => void;
  reloadKey: number;
  triggerReload: () => void;
}

const BuilderContext = createContext<BuilderContextValue | null>(null);

export const useBuilderContext = () => {
  const ctx = useContext(BuilderContext);
  if (!ctx) {
    throw new Error("useBuilderContext must be used within BuilderProvider");
  }
  return ctx;
};

export const useBuilderEventBus = () => {
  const ctx = useBuilderContext();
  return ctx.eventBus;
};

export const useBuilderScene = () => {
  const ctx = useBuilderContext();
  return ctx.scene;
};

export const useBuilderSceneManager = () => {
  const ctx = useBuilderContext();
  return ctx.sceneManager;
};

interface BuilderProviderProps {
  children: ReactNode;
}

export const BuilderProvider = ({ children }: BuilderProviderProps) => {
  const eventBus = useMemo(() => new EventBus(), []);
  const [scene, setScene] = useState<Scene | null>(null);
  const [sceneManager, setSceneManager] = useState<BuilderSceneManager | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);

  const value = useMemo<BuilderContextValue>(
    () => ({
      eventBus,
      scene,
      setScene,
      sceneManager,
      setSceneManager,
      reloadKey,
      triggerReload,
    }),
    [eventBus, scene, sceneManager, reloadKey, triggerReload],
  );

  return <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>;
};

export { BuilderContext };
